import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './JobCard.css';

const JobCard = ({ job, onQuickApply, onViewDetails, onRefer, candidateSkills = [], candidateSkillsWithRatings = [], skillIdToNameMap = {} }) => {
    const { isAuthenticated, showSignIn } = useAuth();
    const navigate = useNavigate();
    const [salaryBreakdownExpanded, setSalaryBreakdownExpanded] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);
    
    // Use job data directly without translation for now
    const jobName = job.jobName || job.title || 'Job Title Not Available';
    const cityName = job.cityName || job.location || '';
    // const companyName = job.companyName || 'Company Name Not Available';
    
    // Get salary data from API
    const minSalary = job.minimumCtc || '';
    const maxSalary = job.maximumCtc || '';
    const isSingleOffer = job.isSingleOfferCtc === true;
    
    // Debug: Check if salaryBreakdown exists
    React.useEffect(() => {
        console.log('Job data:', job);
        console.log('Has salaryBreakdown?', !!job.salaryBreakdown);
        if (job.salaryBreakdown) {
            console.log('Salary breakdown data:', job.salaryBreakdown);
        }
    }, [job]);
    
    // Safely destructure job properties with defaults using API field names
    const {
        pincode = '',
        skills: apiSkills,
        tags: apiTags,
        skillTags,
        jobSkills,
        skill_list,
        tag_list,
    } = job || {};
    // Normalize incoming fields
    const skills = apiSkills || jobSkills || skillTags || skill_list || [];
    const tags = apiTags || tag_list || [];
    
    // Format location from cityName and pincode
    const formattedLocation = cityName && pincode ? 
        `${cityName} - ${pincode}` : 
        (cityName || 'Location Not Specified');
    
    // Handle skills - can be comma-separated string or array
    const processSkills = (skillsData) => {
        const placeholders = new Set([
            'general', 'general skills', 'general skill',
            'other', 'others', 'misc', 'miscellaneous',
            'none', 'no skills', 'not applicable', 'n/a', 'na', 'n a',
            '-', 'n.a.', 'n.a'
        ]);
        let list = [];
        if (Array.isArray(skillsData)) {
            // If it's already an array, flatten and split any comma-separated items
            list = skillsData.flatMap(skill => 
                typeof skill === 'string' ?
                    skill.split(',').map(s => s.trim()) :
                    // If objects are provided, try common name fields
                    (skill && typeof skill === 'object' ? [
                        skill.name, skill.label, skill.title, skill.value
                    ].find(v => typeof v === 'string') || '' : String(skill))
            );
        } else if (typeof skillsData === 'string' && skillsData.trim()) {
            // If it's a comma-separated string, split it
            list = skillsData.split(',').map(skill => skill.trim());
        }
        // Normalize: trim, drop empties, remove placeholders, and de-duplicate
        const cleaned = [];
        const seen = new Set();
        for (const s of list) {
            const val = String(s || '').replace(/[_\s]+/g, ' ').trim();
            if (!val) continue;
            const key = val.toLowerCase();
            if (placeholders.has(key)) continue;
            if (seen.has(key)) continue;
            seen.add(key);
            cleaned.push(val);
        }
        return cleaned;
    };
    
    // Process skills from API (prioritize 'skills' field, fallback to 'tags')
    let jobTags = processSkills(skills);
    if (jobTags.length === 0) {
        jobTags = processSkills(tags);
    }

    const handleRefer = () => {
        if (!isAuthenticated) {
            showSignIn();
            return;
        }
        if (onRefer) {
            onRefer(job);
        }
    };

    const handleApply = () => {
        // Open job details modal instead of direct application
        if (onViewDetails) {
            onViewDetails(job);
        }
    };

    // Calculate skill matching based on budgetSkillsWithRatings
    const calculateSkillMatching = () => {
        if (!isAuthenticated || candidateSkills.length === 0) return null;
        
        // Get budget skills from API response (from searchJobs API)
        const budgetSkills = job.budgetSkillsWithRatings || [];
        
        if (budgetSkills.length === 0) {
            return null;
        }
        
        // Create a map of budget skills with their ratings: skillName -> {rating, skillId}
        const budgetSkillsMap = {};
        budgetSkills.forEach(bs => {
            const skillName = skillIdToNameMap[bs.skillId];
            if (skillName) {
                budgetSkillsMap[skillName.toLowerCase()] = {
                    rating: bs.rating || 5, // Default to 5 if no rating provided
                    skillId: bs.skillId
                };
            }
        });
        
        // Create a map of candidate skills with their ratings: skillName -> rating
        const candidateSkillsMap = {};
        candidateSkillsWithRatings.forEach(cs => {
            const skillName = cs.skillName || cs.name;
            const rating = cs.rating || cs.skillRating || 0;
            if (skillName) {
                candidateSkillsMap[skillName.toLowerCase()] = rating;
            }
        });
        
        console.log(`[JobCard] Comparing budget skills with candidate skills for job: ${jobName}`);
        console.log(`[JobCard] Budget skills map:`, budgetSkillsMap);
        console.log(`[JobCard] Candidate skills map:`, candidateSkillsMap);
        
        // Count matches and track rating comparisons
        let matchedCount = 0;
        const skillRatingInfo = {}; // skillName -> {budgetRating, candidateRating, percentage}
        
        Object.keys(budgetSkillsMap).forEach(budgetSkillName => {
            const isMatch = candidateSkills.some(
                candidateSkill => candidateSkill.trim().toLowerCase() === budgetSkillName
            );
            
            if (isMatch) {
                matchedCount++;
                const budgetRating = budgetSkillsMap[budgetSkillName].rating;
                const candidateRating = candidateSkillsMap[budgetSkillName] || 0;
                const percentage = budgetRating > 0 ? Math.min((candidateRating / budgetRating) * 100, 100) : 0;
                
                skillRatingInfo[budgetSkillName] = {
                    budgetRating,
                    candidateRating,
                    percentage: Math.round(percentage)
                };
                
                console.log(`[JobCard] ✓ Matched skill: ${budgetSkillName} - Budget: ${budgetRating}, Candidate: ${candidateRating}, Fill: ${percentage}%`);
            }
        });
        
        console.log(`[JobCard] Total matches: ${matchedCount}/${budgetSkills.length}`);
        
        return {
            budgetSkillsMap,
            skillRatingInfo,
            matchedCount,
            totalBudgetSkills: budgetSkills.length
        };
    };

    const skillMatching = calculateSkillMatching();

    // Handler for navigating to profile and scrolling to skills section
    const handleYourSkillsClick = () => {
        // Navigate with state to trigger scroll in Profile component
        navigate('/profile', { state: { scrollToSkills: true } });
    };


    return (
        <div className="job-card" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1 }}>
                <h3 className="job-title">{jobName}</h3>
                
                {/* Location and Salary Section - Single Row */}
            <div style={{
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '12px'
            }}>
                <div className="location-salary-container" style={{
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'center'
                }}>
                    {/* Location Section */}
                    <div style={{ 
                        flex: '1 1 50%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        minWidth: 0
                    }}>
                        <div style={{
                            backgroundColor: '#dbeafe',
                            borderRadius: '8px',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <i className="fas fa-map-marker-alt" style={{ color: '#3b82f6', fontSize: '18px' }}></i>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px', fontWeight: '500' }}>Location</div>
                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {formattedLocation}
                            </div>
                        </div>
                    </div>
                    
                    {/* Salary Section */}
                    <div style={{ 
                        flex: '1 1 50%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        minWidth: 0
                    }}>
                        <div style={{
                            backgroundColor: '#dcfce7',
                            borderRadius: '8px',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <i className="fas fa-indian-rupee-sign" style={{ color: '#16a34a', fontSize: '18px' }}></i>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '10.5px', color: '#6b7280', marginBottom: '2px', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Expected Monthly Salary</div>
                            <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#111827' }}>
                                {isSingleOffer && maxSalary ? (
                                    `Approx ₹${Math.round(Number(maxSalary) / 12).toLocaleString()}`
                                ) : minSalary && maxSalary && minSalary !== maxSalary ? (
                                    `₹${Math.round(Number(minSalary) / 12).toLocaleString()} - ₹${Math.round(Number(maxSalary) / 12).toLocaleString()}`
                                ) : maxSalary ? (
                                    `₹${Math.round(Number(maxSalary) / 12).toLocaleString()}`
                                ) : (
                                    'Not Disclosed'
                                )}
                            </div>
                        </div>
                        {/* Expand/Collapse Button */}
                        {job.salaryBreakdown && (
                            <button
                                className={(!salaryBreakdownExpanded && !isAnimating) ? "fast-tooltip" : ""}
                                data-tooltip="View Breakdown"
                                onClick={() => {
                                    console.log('Salary breakdown clicked:', job.salaryBreakdown);
                                    console.log('Current expanded state:', salaryBreakdownExpanded);
                                    setIsAnimating(true);
                                    setSalaryBreakdownExpanded(!salaryBreakdownExpanded);
                                    setTimeout(() => setIsAnimating(false), 300);
                                }}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'transform 0.2s ease',
                                    transform: salaryBreakdownExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    flexShrink: 0
                                }}
                            >
                                <i className="fas fa-chevron-down" style={{ color: '#6b7280', fontSize: '16px' }}></i>
                            </button>
                        )}
                    </div>
                </div>
                
                {/* Salary Breakdown Table - Expandable within same chip */}
                {salaryBreakdownExpanded && job.salaryBreakdown && job.salaryBreakdown.minimumSalary && job.salaryBreakdown.maximumSalary && (
                    <div style={{
                        paddingTop: '10px',
                        paddingLeft: '50px',
                        paddingRight: '48px',
                        marginTop: '10px',
                        borderTop: '1px solid #e5e7eb'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '8px'
                        }}>
                            {/* Fixed CTC Row */}
                            {job.salaryBreakdown.minimumSalary.fixedCtc && job.salaryBreakdown.maximumSalary.fixedCtc && (
                                <div className="salary-breakdown-row" style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    paddingBottom: '8px',
                                    borderBottom: '1px solid #e5e7eb'
                                }}>
                                    <span style={{ 
                                        fontSize: '12px', 
                                        fontWeight: '500', 
                                        color: '#6b7280' 
                                    }}>
                                        Fixed
                                    </span>
                                    <span style={{ 
                                        fontSize: '13px', 
                                        fontWeight: '700', 
                                        color: '#111827'
                                    }}>
                                        {isSingleOffer ? (
                                            `₹${Math.round(job.salaryBreakdown.maximumSalary.fixedCtc / 12).toLocaleString()}`
                                        ) : (
                                            `₹${Math.round(job.salaryBreakdown.minimumSalary.fixedCtc / 12).toLocaleString()} - ₹${Math.round(job.salaryBreakdown.maximumSalary.fixedCtc / 12).toLocaleString()}`
                                        )}
                                    </span>
                                </div>
                            )}

                            {/* Net Take Home Row */}
                            {job.salaryBreakdown.minimumSalary.netTakeHome && job.salaryBreakdown.maximumSalary.netTakeHome && (
                                <div className="salary-breakdown-row" style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    paddingBottom: '8px',
                                    borderBottom: '1px solid #e5e7eb'
                                }}>
                                    <span style={{ 
                                        fontSize: '12px', 
                                        fontWeight: '500', 
                                        color: '#6b7280' 
                                    }}>
                                        Net Take Home
                                    </span>
                                    <span style={{ 
                                        fontSize: '13px', 
                                        fontWeight: '700', 
                                        color: '#111827'
                                    }}>
                                        {isSingleOffer ? (
                                            `₹${Math.round(job.salaryBreakdown.maximumSalary.netTakeHome / 12).toLocaleString()}`
                                        ) : (
                                            `₹${Math.round(job.salaryBreakdown.minimumSalary.netTakeHome / 12).toLocaleString()} - ₹${Math.round(job.salaryBreakdown.maximumSalary.netTakeHome / 12).toLocaleString()}`
                                        )}
                                    </span>
                                </div>
                            )}

                            {/* Variable CTC Row */}
                            {job.salaryBreakdown.minimumSalary.variableCtc !== null && 
                             job.salaryBreakdown.minimumSalary.variableCtc !== undefined && 
                             job.salaryBreakdown.maximumSalary.variableCtc !== null && 
                             job.salaryBreakdown.maximumSalary.variableCtc !== undefined && (
                                <div className="salary-breakdown-row" style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center'
                                }}>
                                    <span style={{ 
                                        fontSize: '12px', 
                                        fontWeight: '500', 
                                        color: '#6b7280' 
                                    }}>
                                        Variable
                                    </span>
                                    <span style={{ 
                                        fontSize: '13px', 
                                        fontWeight: '700', 
                                        color: '#111827'
                                    }}>
                                        {isSingleOffer ? (
                                            `₹${Math.round(job.salaryBreakdown.maximumSalary.variableCtc / 12).toLocaleString()}`
                                        ) : (
                                            `₹${Math.round(job.salaryBreakdown.minimumSalary.variableCtc / 12).toLocaleString()} - ₹${Math.round(job.salaryBreakdown.maximumSalary.variableCtc / 12).toLocaleString()}`
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            
            {/* Skills Section */}
            {jobTags.length > 0 && (
                <div style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    padding: '12px',
                    marginTop: '12px'
                }}>
                    <div style={{ marginBottom: '8px' }}>
                        <span style={{ 
                            fontSize: '11px', 
                            fontWeight: '600', 
                            color: '#000'
                        }}>
                            Job Skills Required 
                        </span>
                        {skillMatching && candidateSkills.length > 0 && (() => {
                            const matchedCount = skillMatching.matchedCount;
                            const totalBudgetSkills = skillMatching.totalBudgetSkills;
                            const matchPercentage = totalBudgetSkills > 0 ? Math.round((matchedCount / totalBudgetSkills) * 100) : 0;
                            
                            return (
                                <span style={{ fontSize: '11px', fontWeight: '600', color: 'rgba(0, 0, 0, 0.7)' }}>
                                    ( <span style={{ 
                                        fontWeight: 'bold',
                                        color: matchPercentage >= 80 ? '#22c55e' : matchPercentage >= 60 ? '#f97316' : '#ef4444'
                                    }}>{matchedCount}/{totalBudgetSkills}</span> matched skills with <span 
                                        onClick={handleYourSkillsClick}
                                        style={{ 
                                            fontWeight: 'bold', 
                                            color: '#2563eb',
                                            cursor: 'pointer',
                                            textDecoration: 'none'
                                        }}
                                        onMouseEnter={(e) => e.target.style.color = '#194ab9'}
                                        onMouseLeave={(e) => e.target.style.color = '#2563eb'}
                                    >your skills</span> )
                                </span>
                            );
                        })()}
                    </div>
                    <div className="job-tags" style={{ marginBottom: 0 }}>
                        {jobTags.map((skill, index) => {
                            const skillLower = String(skill).trim().toLowerCase();
                            let ratingInfo = null;

                            if (skillMatching && skillMatching.skillRatingInfo) {
                                ratingInfo = skillMatching.skillRatingInfo[skillLower];
                            }

                            const isMatchedSkill = Boolean(ratingInfo) || candidateSkills.some(
                                candidateSkill => candidateSkill.trim().toLowerCase() === skillLower
                            );
                            
                            const chipClassName = `job-tag ${isMatchedSkill ? 'job-tag-matched' : 'job-tag-unmatched'}`;
                            const chipStyle = isMatchedSkill
                                ? ratingInfo
                                    ? {
                                        background: `linear-gradient(to right, rgba(37, 99, 235, 0.15) ${ratingInfo.percentage}%, #fff ${ratingInfo.percentage}%)`
                                      }
                                    : undefined
                                : {
                                    borderColor: '#6b7280',
                                    color: '#6b7280'
                                  };

                            return (
                                <span
                                    key={index}
                                    className={chipClassName}
                                    style={chipStyle}
                                    title={ratingInfo ? `Your rating: ${ratingInfo.candidateRating}/${ratingInfo.budgetRating}` : undefined}
                                >
                                    {String(skill)}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
            </div>
            <div className="job-actions" style={{ marginTop: '12px' }}>
                <button className="btn btn-outline btn-refer btn-sm" onClick={handleRefer}>
                    <i className="fas fa-user-plus"></i>
                    Refer & Earn
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleApply}>
                    <i className="fas fa-paper-plane"></i>
                    Apply Now
                </button>
            </div>
        </div>
    );
};

export default JobCard;
